import { useCallback } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { SELLER_KEYS } from './useSellers'
import { getBrandDetails, getBrands, getMemberDetails, getMembers } from '../api/member/organization'
import { getEbayBrandTokens, getEbayMemberTokens } from '../api/react/system/system'
import {
  addWhitelistedDomain,
  addWhitelistedSeller,
  checkDynamicsId,
  createBrand,
  createBrandExtraData,
  createDebtor,
  createMember,
  createMemberExtraData,
  deleteBrand,
  deleteBrandExtraData,
  deleteDebtor,
  deleteMember,
  deleteMemberContact,
  deleteMemberExtraData,
  editMember,
  getBrandExtraData,
  getBrandIprs,
  getDebtors,
  getMemberExtraData,
  getOrganizationsTable,
  updateBrand,
  updateBrandDetails,
  updateMemberLogo,
  uploadBrandLogo,
  getSectors,
} from '../api/react/organizations/organizations'
import type { BrandsFilters, MembersFilters } from '../types/organization.types'

export const ORGANIZATION_KEYS = {
  debtors: (memberId: string) => ['debtors', memberId] as const,
  dynamicsId: (params: {
    dynamics_id: string
    member_id?: string
    brand_id?: string
    type: 'members' | 'brands' | 'debtors'
  }) => ['dynamicsId', params] as const,
  memberDetail: (id: string) => ['memberDetail', id] as const,
  brandDetail: (id: string) => ['brandDetail', id] as const,
  brands: (filters: BrandsFilters) => ['brands', filters] as const,
  members: (filters: MembersFilters) => ['members', filters] as const,
  organizationsTable: (option: number) => ['organizationsTable', option] as const,
  brandExtraData: (id: string, type: string, params?: object) => ['brandExtraData', id, type, params] as const,
  brandIprs: (brandId: string) => ['brandIprs', brandId] as const,
  whitelistedSellers: (brandId: string) => ['whitelistedSellers', brandId] as const,
  whitelistedDomains: (brandId: string) => ['whitelistedDomains', brandId] as const,
  ebayMemberTokens: () => ['ebayMemberTokens'] as const,
  ebayBrandTokens: () => ['ebayBrandTokens'] as const,
}

// GET /members/:id/
export const useMemberDetail = (id: string) => {
  return useQuery({
    queryKey: ORGANIZATION_KEYS.memberDetail(id),
    queryFn: () => getMemberDetails(id),
    enabled: !!id,
  })
}

// GET /members/brands/:id/
export const useBrandDetail = (id: string) => {
  return useQuery({
    queryKey: ORGANIZATION_KEYS.brandDetail(id),
    queryFn: () => getBrandDetails(id),
    enabled: !!id,
  })
}

// GET /members/brands/
export const useBrands = (filters: BrandsFilters, enabled?: boolean) => {
  return useQuery({
    queryKey: ORGANIZATION_KEYS.brands(filters),
    queryFn: () => getBrands(filters),
    placeholderData: (prev) => prev,
    ...(enabled !== undefined && { enabled }),
  })
}

// GET /members/brands/ (infinite scroll)
export const useInfiniteMembers = (filters: Omit<MembersFilters, 'page'>) => {
  return useInfiniteQuery({
    queryKey: ORGANIZATION_KEYS.members({ ...filters, page: 0 }),
    queryFn: ({ pageParam }: { pageParam: number | null }) =>
      getMembers({ ...filters, page: pageParam ?? 1 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
  })
}

export const useInfiniteBrands = (filters: Omit<BrandsFilters, 'page'>) => {
  return useInfiniteQuery({
    queryKey: ORGANIZATION_KEYS.brands({ ...filters, page: 0 }),
    queryFn: ({ pageParam }: { pageParam: number | null }) =>
      getBrands({ ...filters, page: pageParam ?? 1 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: { nextPage?: number }) => lastPage.nextPage ?? undefined,
  })
}

// GET /members/
export const useMembers = (filters: MembersFilters, enabled?: boolean) => {
  return useQuery({
    queryKey: ORGANIZATION_KEYS.members(filters),
    queryFn: () => getMembers(filters),
    placeholderData: (prev) => prev,
    ...(enabled !== undefined && { enabled }),
  })
}

// GET /organizations/{members|platforms}/table
export const useOrganizationsTable = (option: number) => {
  return useQuery({
    queryKey: ORGANIZATION_KEYS.organizationsTable(option),
    queryFn: () => getOrganizationsTable({ option }),
  })
}

// POST /members/brands/
export const useCreateBrand = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createBrand,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brands'] }),
  })
}

// PATCH /members/brands/:id/
export const useUpdateBrand = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: updateBrand,
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['brands'] })
      qc.invalidateQueries({ queryKey: ORGANIZATION_KEYS.brandDetail(variables.id) })
    },
  })
}

// DELETE /members/brands/:id/
export const useDeleteBrand = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteBrand,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brands'] }),
  })
}

// PUT /members/brands/:id/logo/
export const useUploadBrandLogo = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: uploadBrandLogo,
    onSuccess: (_data, variables) => qc.invalidateQueries({ queryKey: ORGANIZATION_KEYS.brandDetail(variables.id) }),
  })
}

// GET /members/:id/:type/
export const useMemberExtraData = (id: string, type: string) => {
  return useQuery({
    queryKey: ['memberExtraData', id, type],
    queryFn: () => getMemberExtraData({ id, type }),
    enabled: !!id,
  })
}

// GET /members/brands/:id/:type/
export const useBrandExtraData = (id: string, type: string, params?: { page_size?: number; search?: string }) => {
  return useQuery({
    queryKey: ORGANIZATION_KEYS.brandExtraData(id, type, params),
    queryFn: () => getBrandExtraData({ id, type, ...params }),
    enabled: !!id,
  })
}

// GET /members/brands/:id/iprs/
export const useBrandIprs = (brandId: string, params?: { search?: string; page_size?: number }) => {
  return useQuery({
    queryKey: [...ORGANIZATION_KEYS.brandIprs(brandId), params],
    queryFn: () => getBrandIprs({ brandId, ...params }),
    enabled: !!brandId,
  })
}

// POST /members/brands/:id/:type/
export const useCreateBrandExtraData = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createBrandExtraData,
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: ORGANIZATION_KEYS.brandExtraData(variables.id, variables.type) }),
  })
}

// DELETE /members/brands/:id/:type/:data_id
export const useDeleteBrandExtraData = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteBrandExtraData,
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: ORGANIZATION_KEYS.brandExtraData(variables.id, variables.type) }),
  })
}

// POST /members/brands/:brandId/whitelisted/domains/
export const useAddWhitelistedDomain = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ brandId, domain }: { brandId: string; domain: string }) => addWhitelistedDomain(brandId, domain),
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: ORGANIZATION_KEYS.whitelistedDomains(variables.brandId) }),
  })
}

// POST /members/brands/:brandId/whitelisted/sellers/
export const useAddWhitelistedSeller = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ brandId, sellerId }: { brandId: string; sellerId: string }) =>
      addWhitelistedSeller(brandId, sellerId),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ORGANIZATION_KEYS.whitelistedSellers(variables.brandId) })
      qc.invalidateQueries({ queryKey: SELLER_KEYS.all })
    },
  })
}

// ── Member mutations ──────────────────────────────

// POST /members/
export const useCreateMember = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createMember,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
  })
}

// PATCH /members/:id/
export const useEditMember = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: editMember,
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['members'] })
      qc.invalidateQueries({ queryKey: ORGANIZATION_KEYS.memberDetail(variables.id) })
    },
  })
}

// DELETE /members/:id/
export const useDeleteMember = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteMember,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
  })
}

// PUT /members/:id/logo/
export const useUpdateMemberLogo = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: updateMemberLogo,
    onSuccess: (_data, variables) => qc.invalidateQueries({ queryKey: ORGANIZATION_KEYS.memberDetail(variables.id) }),
  })
}

// DELETE /members/:member_id/contacts/:id/
export const useDeleteMemberContact = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteMemberContact,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['memberDetail'] }),
  })
}

// ── Member extra data ─────────────────────────────

// POST /members/:id/:type/
export const useCreateMemberExtraData = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createMemberExtraData,
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: ['memberExtraData', variables.id, variables.type] }),
  })
}

// DELETE /members/:id/:type/:data_id
export const useDeleteMemberExtraData = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteMemberExtraData,
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: ['memberExtraData', variables.id, variables.type] }),
  })
}

// ── Debtors ───────────────────────────────────────

// GET /members/:member_id/debtors/
export const useDebtors = (memberId: string) => {
  return useQuery({
    queryKey: ORGANIZATION_KEYS.debtors(memberId),
    queryFn: () => getDebtors({ member_id: memberId }),
    enabled: !!memberId,
  })
}

// POST /members/:member_id/debtors/
export const useCreateDebtor = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createDebtor,
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: ORGANIZATION_KEYS.debtors(variables.member_id) }),
  })
}

// DELETE /members/:member_id/debtors/:id/
export const useDeleteDebtor = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteDebtor,
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: ORGANIZATION_KEYS.debtors(variables.member_id) }),
  })
}

// ── Dynamics ID check ─────────────────────────────

// GET /{type}/dynamics_id/
export const useCheckDynamicsId = (
  params: {
    dynamics_id: string
    member_id?: string
    brand_id?: string
    type: 'members' | 'brands' | 'debtors'
  },
  enabled: boolean
) => {
  return useQuery({
    queryKey: ORGANIZATION_KEYS.dynamicsId(params),
    queryFn: () => checkDynamicsId(params),
    enabled: enabled && !!params.dynamics_id,
  })
}

// PATCH /members/brands/:id/ (whitelisted_seller_ids / whitelisted_domains)
export const useUpdateBrandDetails = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Parameters<typeof updateBrandDetails>[0]) => updateBrandDetails(data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ORGANIZATION_KEYS.brandDetail(variables.id) })
      qc.invalidateQueries({ queryKey: ['brands'] })
    },
  })
}

// GET /settings/ebay/member_tokens/
export const useEbayMemberTokens = (enabled: boolean) => {
  return useQuery({
    queryKey: ORGANIZATION_KEYS.ebayMemberTokens(),
    queryFn: getEbayMemberTokens,
    enabled,
  })
}

// GET /settings/ebay/brand_tokens/
export const useEbayBrandTokens = (enabled: boolean) => {
  return useQuery({
    queryKey: ORGANIZATION_KEYS.ebayBrandTokens(),
    queryFn: getEbayBrandTokens,
    enabled,
  })
}

export const usePrefetchMembers = () => {
  const qc = useQueryClient()
  return (filters: Parameters<typeof getMembers>[0]) =>
    qc.prefetchQuery({
      queryKey: ORGANIZATION_KEYS.members(filters),
      queryFn: () => getMembers(filters),
      staleTime: 5 * 60 * 1000,
    })
}

export const useInvalidateWhitelistedSellers = () => {
  const qc = useQueryClient()
  return useCallback((brandId: string) => qc.invalidateQueries({ queryKey: ORGANIZATION_KEYS.whitelistedSellers(brandId) }), [qc])
}

export const useInvalidateWhitelistedDomains = () => {
  const qc = useQueryClient()
  return useCallback((brandId: string) => qc.invalidateQueries({ queryKey: ORGANIZATION_KEYS.whitelistedDomains(brandId) }), [qc])
}

// ── Sectors ──────────────────────────────────────

export const useGetSectors = (
  params: { search?: string; page?: number; page_size?: number },
  enabled = true,
) => {
  return useQuery({
    queryKey: ['sectors', params],
    queryFn: () => getSectors(params),
    enabled,
    staleTime: 60_000,
  })
}

